import { array, boolean, mixed, object, string } from 'yup';
import type {
  TAnnouncement,
  TAnnouncementAudience,
} from '@src/shared/types/announcement';
import type { TMedia } from '@src/shared/types/media';
import type { TAnnouncementShareToProgram } from '../../utils/announcement-audience';

export type TAnnouncementMediaType = 'image' | 'video';

export type TAnnouncementFormValues = {
  name: string;
  description: string;
  mediaType: TAnnouncementMediaType;
  mediaFile: File | null;
  existingMedia: TMedia | null;
  showCta: boolean;
  cta_label: string;
  cta_link: string;
  is_active: boolean;
  audience: TAnnouncementAudience | '';
  shareToPrograms: TAnnouncementShareToProgram[];
  scheduleShare: boolean;
  shared_at: string;
  expire_at: string;
};

export const createAnnouncementInitialValues = (
  announcement?: TAnnouncement
): TAnnouncementFormValues => {
  const mimeType = announcement?.media?.mime_type ?? '';
  const mediaType: TAnnouncementMediaType = mimeType.startsWith('video/')
    ? 'video'
    : 'image';

  const hasSchedule =
    announcement?.status === 'SCHEDULED' || Boolean(announcement?.share_at);

  const audience = announcement?.audience ?? '';

  return {
    name: announcement?.name ?? '',
    description: announcement?.description ?? '',
    mediaType,
    mediaFile: null,
    existingMedia: announcement?.media ?? null,
    showCta: Boolean(announcement?.cta_label || announcement?.cta_link),
    cta_label: announcement?.cta_label ?? '',
    cta_link: announcement?.cta_link ?? '',
    is_active: announcement?.is_active ?? true,
    audience,
    shareToPrograms:
      audience === 'PROGRAMS'
        ? (announcement?.programs?.map((program) => ({
            id: program.id,
            name: program.name,
          })) ?? [])
        : [],
    scheduleShare: hasSchedule,
    shared_at: announcement?.share_at?.split('T')[0] ?? '',
    expire_at: announcement?.expire_at?.split('T')[0] ?? '',
  };
};

export const announcementFormSchema = object().shape({
  name: string().required('Announcement name is required'),
  description: string().nullable(),
  mediaType: string().oneOf(['image', 'video']).required(),
  mediaFile: mixed<File>().nullable(),
  existingMedia: mixed().nullable(),
  showCta: boolean(),
  cta_label: string().when('showCta', {
    is: true,
    then: (schema) => schema.required('Button name is required'),
    otherwise: (schema) => schema.nullable(),
  }),
  cta_link: string().when('showCta', {
    is: true,
    then: (schema) =>
      schema.required('Redirection link is required').url('Enter a valid URL'),
    otherwise: (schema) => schema.nullable(),
  }),
  is_active: boolean(),
  audience: string()
    .oneOf(['ALL', 'LEADS', 'PARTICIPANTS', 'PROGRAMS'], 'Select an audience')
    .required('Select an audience')
    .test(
      'programs-required',
      'Select at least one program',
      function validatePrograms(value) {
        const { shareToPrograms } = this.parent as {
          shareToPrograms?: TAnnouncementShareToProgram[];
        };

        if (value !== 'PROGRAMS') return true;
        return (shareToPrograms?.length ?? 0) > 0;
      }
    ),
  shareToPrograms: array().of(
    object({
      id: mixed<number>().required(),
      name: string().required(),
    })
  ),
  scheduleShare: boolean(),
  shared_at: string().when('scheduleShare', {
    is: true,
    then: (schema) => schema.required('Share date is required'),
    otherwise: (schema) => schema.nullable(),
  }),
  expire_at: string().when('scheduleShare', {
    is: true,
    then: (schema) =>
      schema
        .required('Expire date is required')
        .test(
          'after-share-date',
          'Expire date must be on or after share date',
          function validateExpireDate(value) {
            const { shared_at } = this.parent as { shared_at?: string };
            if (!value || !shared_at) return true;
            return value >= shared_at.split('T')[0];
          }
        ),
    otherwise: (schema) => schema.nullable(),
  }),
});
